// Settings › Campaigns › Your website (BL-092): the company's sites that may send, whether the tag shows
// each campaign's tracking number, and the number for each campaign as its ads' URLs name it
// (adSessions.setup and adSessions.setSite).

export interface SiteSetup {
  origins: string[];
  swap: boolean;
  numbers: { key: string; phone: string }[];
}
export interface SiteWeek {
  visits: number;
  calls: number;
  leads: number;
}
export interface SiteAnswer {
  site: SiteSetup;
  week: SiteWeek;
  tagPath: string;
}
export interface SiteDraft {
  origins: string;
  swap: boolean;
  numbers: { key: string; phone: string }[];
}

export const draftOf = (s: SiteSetup): SiteDraft => ({
  origins: s.origins.join('\n'),
  swap: s.swap,
  numbers: s.numbers.map((n) => ({ ...n })),
});

/** A site as it was typed (plumber.example, https://www.plumber.example/contact) as its browser names it. */
export function originOf(typed: string): string | null {
  const t = typed.trim();
  if (!t) return null;
  try {
    const u = new URL(/^https?:\/\//i.test(t) ? t : `https://${t}`);
    if (!/^https?:$/.test(u.protocol) || !u.hostname.includes('.')) return null;
    return u.origin.toLowerCase();
  } catch {
    return null;
  }
}

/** The campaign's name as the tag keys it: "Poly B" and poly-b are the same. */
export const keyOf = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);

/** What adSessions.setSite takes, or what to fix first. */
export function siteInput(d: SiteDraft): { input: SiteSetup } | { error: string } {
  const origins: string[] = [];
  for (const line of d.origins.split(/[\n,]+/)) {
    if (!line.trim()) continue;
    const o = originOf(line);
    if (!o) return { error: `“${line.trim()}” isn’t a website address. Type it like plumber.example.` };
    if (!origins.includes(o)) origins.push(o);
  }
  if (origins.length > 10) return { error: 'At most 10 sites.' };
  const numbers: SiteSetup['numbers'] = [];
  for (const n of d.numbers) {
    if (!n.key.trim() && !n.phone) continue;
    const key = keyOf(n.key);
    if (!key) return { error: 'Each number needs its campaign’s name, as the ads’ URLs give it.' };
    if (!n.phone) return { error: `Choose the tracking number for ${n.key.trim()}.` };
    if (numbers.some((x) => x.key === key)) return { error: `${n.key.trim()} is there twice.` };
    numbers.push({ key, phone: n.phone });
  }
  if (d.swap && !numbers.length)
    return { error: 'Add a campaign’s number to show, or keep the tag to capture only.' };
  return { input: { origins, swap: d.swap, numbers } };
}

/** The script tag to paste into the site (or a tag manager), on the app's own address. */
export const tagSnippet = (origin: string, tagPath: string): string =>
  `<script async src="${origin}${tagPath}"></script>`;

/** The last 7 days in a line: 12 visits from ads, 3 became calls, 2 form leads. */
export function weekLine(w: SiteWeek): string {
  if (!w.visits && !w.leads) return 'No visits from ads or form leads in the last 7 days.';
  const visits = `${w.visits} ${w.visits === 1 ? 'visit' : 'visits'} from ads`;
  const calls = `${w.calls} became ${w.calls === 1 ? 'a call' : 'calls'}`;
  const leads = `${w.leads} form ${w.leads === 1 ? 'lead' : 'leads'}`;
  return `Last 7 days: ${visits}, ${calls}, ${leads}.`;
}

/** What the tag does now, in a line. */
export function siteMeans(s: SiteSetup): string {
  if (!s.origins.length) return 'Off: no site is named, so the tag does nothing.';
  return s.swap
    ? 'Keeps each ad click and shows the campaign’s tracking number on the site.'
    : 'Keeps each ad click only: the site’s numbers stay as they are.';
}
